// Libs
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { type UseFormHandleSubmit, type UseFormReturn, useForm } from "react-hook-form";
import { useHistory } from "react-router";

// Api
import { useQueryOrganization, useQueryOrganizations } from "src/api/organizations";
import { useQueryPointOfSalesGroups } from "src/api/pointOfSalesGroups";
import { useQueryPointOfSales, useQueryPointsOfSales } from "src/api/pointsOfSales";
import { useCreateUser, useUpdateUser } from "src/api/users/mutation";
import type { UserDto } from "src/api/users/type";

// Hooks
import { useTranslation } from "src/design-system/core/TranslationContext";
import useAuth from "src/hooks/useAuth";

// Utils
import { PATH_HOME } from "src/routesConstants";
import { USER_CREATE_FORM_TK, USER_UPDATE_FORM_TK } from "src/views/admin/AdminView/user/config";
import { ADMIN_TRANSLATION_NAMESPACE } from "src/views/admin/config";
import {
    availablePointOfSalesGroups,
    availablePointsOfSales,
    useFormSchema,
} from "./UserFormView.logic";

// Types
import type { Id } from "src/types/id";
import type { Organization } from "src/types/organization";
import type { Pos } from "src/types/pos";
import type { PosGroup } from "src/types/posGroup";
import { UserRoleEnum } from "src/types/role";
import { type User, UserKeys } from "src/types/user";

interface UserFormType {
    role: UserRoleEnum;
    organization: Id | string;
    poss: Id[];
    posGroups: Id[];
    firstName: string;
    lastName: string;
    email: string;
}

interface UserFormHook {
    controller: UseFormReturn<UserFormType>;
    organizations: Organization[];
    poss: Pos[];
    posGroups: PosGroup[];
    isSubmitLoading: boolean;
    error: string;
    handleViewAsClick: (userId: Id) => () => void;
    handleSubmit: UseFormHandleSubmit<UserFormType>;
    handlePossSelect: (newPoss: Id[]) => void;
    handlePosGroupSelect: (newPosGroups: Id[]) => void;
    onSubmit: (data: UserFormType) => Promise<void>;
}

export const VIEW_AS_ADMIN_LOCAL_PARAM = "admin_column_display";

interface UserFormViewHookProps {
    user?: User & { organizationId?: number };
    organizationId?: Id;
    posId?: Id;
}

export const useUserForm = (props?: UserFormViewHookProps): UserFormHook => {
    const { user, organizationId, posId } = props || {};
    const { data: organization } = organizationId
        ? useQueryOrganization(organizationId)
        : { data: undefined };
    const { data: pos } = posId ? useQueryPointOfSales(posId) : { data: undefined };
    const { enqueueSnackbar } = useSnackbar();
    const { viewAs } = useAuth();
    const history = useHistory();
    const schema = useFormSchema();
    const { t: translationCreateUser } = useTranslation(
        `${ADMIN_TRANSLATION_NAMESPACE}:${USER_CREATE_FORM_TK}`
    );
    const { t: translationUpdateUser } = useTranslation(
        `${ADMIN_TRANSLATION_NAMESPACE}:${USER_UPDATE_FORM_TK}`
    );
    const defaultValues = user
        ? {
              role: user.role,
              organization: user.organizationId,
              poss: user.poss.map((e) => e.id),
              posGroups: user.posGroups?.map((e) => e.id),
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
          }
        : {
              role: UserRoleEnum.ADMIN,
              organization: organization?.id || "",
              poss: pos ? [pos.id] : [],
              posGroups: [],
              firstName: "",
              lastName: "",
              email: "",
          };
    const controller = useForm<UserFormType>({
        defaultValues,
        resolver: schema,
    });

    useEffect(() => {
        controller.reset(defaultValues);
    }, [user, organization, pos]);

    const { data: organizations } = useQueryOrganizations();
    const { data: pointsOfSales } = useQueryPointsOfSales({
        enabled: true,
        activatedOnly: false,
        organizationId,
    });
    const { data: pointOfSalesGroups } = useQueryPointOfSalesGroups(organizationId);
    const { mutateAsync: createUser, isPending: isCreateLoading } = useCreateUser();
    const { mutateAsync: updateUser, isPending: isUpdateLoading } = useUpdateUser();

    const [error, setError] = useState("");
    const { handleSubmit } = controller;

    const onSubmit = async (form: UserFormType) => {
        const userData: UserDto = {
            role: form.role,
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
        };

        if (userData.role !== UserRoleEnum.SUPERADMIN) {
            const organization = Number(form.organization);
            const poss = availablePointsOfSales(organization, pointsOfSales)
                .filter((e) => form.poss.includes(e.id))
                .map((e) => e.id);
            const posGroups = availablePointOfSalesGroups(organization, pointOfSalesGroups)
                .filter((e) => form.posGroups.includes(e.id))
                .map((e) => e.id);
            userData.organization = Number(form.organization);
            userData.poss = poss || [];
            userData.posGroups = posGroups || [];
        }

        try {
            if (user) {
                await updateUser({
                    userId: user.id,
                    user: userData,
                });
                enqueueSnackbar(translationUpdateUser("snackbar.success"), { variant: "success" });
            } else {
                const newUser = await createUser(userData);

                enqueueSnackbar(translationCreateUser("snackbar.success"), { variant: "success" });
                history.push({
                    pathname: `../user/${newUser.id}`,
                });
            }
            setError("");
        } catch (e) {
            if (e.status === 401) {
                setError(translationCreateUser("snackbar.error-admin-creation"));
            } else {
                setError(e.message);
            }
        }
    };

    const selectedOrganization = Number(controller.watch(UserKeys.organization));
    const selectedPoss: Id[] = controller.watch(UserKeys.poss);
    const poss = availablePointsOfSales(selectedOrganization, pointsOfSales || []);
    const posGroups = availablePointOfSalesGroups(selectedOrganization, pointOfSalesGroups || []);

    const handleViewAsClick = (userId: number) => async () => {
        localStorage.setItem(`flynt:${VIEW_AS_ADMIN_LOCAL_PARAM}`, "false");
        await viewAs(userId);
        history.push(PATH_HOME);
    };

    const handlePossSelect = (newPoss: Id[]) => {
        const posGroupsIds = controller.getValues(UserKeys.posGroups) as Id[];
        const posGroups = pointOfSalesGroups.filter((e) => posGroupsIds.includes(e.id));

        controller.setValue(
            "posGroups",
            posGroups
                .filter((posGroup) => {
                    let posIsMissing = false;

                    posGroup.poss.forEach((e) => {
                        if (!newPoss.includes(e.id)) {
                            posIsMissing = true;
                        }
                    });
                    return !posIsMissing;
                })
                .map((e) => e.id)
        );
    };

    const handlePosGroupSelect = (newPosGroupsIds: Id[]) => {
        newPosGroupsIds.forEach((selectedPosGroup) => {
            const posGroup = pointOfSalesGroups.find((e) => e.id === selectedPosGroup);
            const newPossIds = selectedPoss;

            posGroup.poss.forEach((pos) => {
                if (!selectedPoss.includes(pos.id)) {
                    newPossIds.push(pos.id);
                }
            });
            controller.setValue("poss", newPossIds);
        });
    };

    return {
        controller,
        organizations: organizations || [],
        poss,
        posGroups,
        isSubmitLoading: isCreateLoading || isUpdateLoading,
        error,
        handleViewAsClick,
        handleSubmit,
        handlePossSelect,
        handlePosGroupSelect,
        onSubmit: onSubmit,
    };
};
